import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import NfcIcon from "@mui/icons-material/Nfc";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type { AuthMethod, Driver, Label, SessionRecord } from "../types";
import { useToast } from "../toast";
import LabelDialog from "../dialogs/LabelDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import AuthMethodDialog from "../dialogs/AuthMethodDialog";

export default function DriverDetail({
  id,
  onBack,
  onOpenSession,
}: {
  id: number;
  onBack: () => void;
  onOpenSession: (sessionId: number) => void;
}) {
  const api = useAuthApi();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingLabel, setAddingLabel] = useState(false);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [deletingLabel, setDeletingLabel] = useState<Label | null>(null);
  const [authMethods, setAuthMethods] = useState<AuthMethod[]>([]);
  const [authMethodDialog, setAuthMethodDialog] = useState<{ open: boolean; method: AuthMethod | null }>({ open: false, method: null });
  const [deletingMethod, setDeletingMethod] = useState<AuthMethod | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, am] = await Promise.all([
        api<Driver>(`${MGMT}/drivers/${id}`),
        api<SessionRecord[]>(`${MGMT}/sessions?driver_id=${id}`).catch(() => null),
        api<AuthMethod[]>(`${MGMT}/drivers/${id}/auth-methods`).catch(() => null),
      ]);
      setDriver(d);
      setSessions(s ?? []);
      setAuthMethods(am ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const deleteLabel = async () => {
    if (!driver || !deletingLabel) return;
    try {
      await api(`${MGMT}/drivers/${driver.id}/labels/${deletingLabel.id}`, { method: "DELETE" });
      toast.show("Label removed");
      setDeletingLabel(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const deleteAuthMethod = async () => {
    if (!driver || !deletingMethod) return;
    try {
      await api(`${MGMT}/drivers/${driver.id}/auth-methods/${deletingMethod.id}`, { method: "DELETE" });
      toast.show("Auth method removed");
      setDeletingMethod(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const authMethodTypeChip = (method: AuthMethod) => {
    if (method.type === "rfid_card") {
      return <Chip icon={<CreditCardIcon />} label="RFID Card" size="small" />;
    }
    if (method.type === "app") {
      return <Chip icon={<SmartphoneIcon />} label="App" size="small" color="primary" />;
    }
    return <Chip icon={<NfcIcon />} label="App RFID" size="small" color="secondary" />;
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (!driver) return <Typography color="error">Driver not found</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{driver.name}</Typography>
        <Chip label={driver.status} color={statusColor(driver.status)} />
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Contact</Typography>
              <Typography>{driver.email}</Typography>
              {driver.phone && <Typography color="text.secondary">{driver.phone}</Typography>}
              <Typography variant="caption" color="text.secondary">
                Created {fmtDate(driver.created_at)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Groups</Typography>
              {(driver.groups ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No groups</Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {(driver.groups ?? []).map((g) => (
                    <Chip
                      key={g.id}
                      label={g.name}
                      size="small"
                      color={g.is_dynamic ? "info" : "default"}
                      variant="outlined"
                      title={g.is_dynamic ? "Dynamic (via label rules)" : "Explicit member"}
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Labels */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Labels</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingLabel(true)}>
              Add Label
            </Button>
          </Stack>
          {(driver.labels ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No labels</Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(driver.labels ?? []).map((lbl) => (
                <Chip
                  key={lbl.id}
                  label={`${lbl.key}=${lbl.value}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => setDeletingLabel(lbl)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Auth Methods */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Auth Methods</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAuthMethodDialog({ open: true, method: null })}>
              Add RFID Card
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Centrally managed methods are read-only. Tenant users can add and manage RFID cards only.
          </Typography>
          {authMethods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No auth methods</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {authMethods.map((m) => {
                    const canManage = m.tenant_id != null;
                    return (
                      <TableRow key={m.id}>
                        <TableCell>{authMethodTypeChip(m)}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>{m.identifier ?? "—"}</TableCell>
                        <TableCell>{m.label ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={m.status}
                            size="small"
                            color={m.status === "active" ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>{m.expiry_date ? fmtDate(m.expiry_date) : "—"}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" disabled={!canManage} onClick={() => setAuthMethodDialog({ open: true, method: m })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" disabled={!canManage} onClick={() => setDeletingMethod(m)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Recent Sessions */}
      {sessions.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Recent Sessions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Session</TableCell>
                    <TableCell>Charger</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell align="right">Energy (kWh)</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.slice(0, 20).map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Button size="small" onClick={() => onOpenSession(s.id)}>
                          #{s.id}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{s.charge_point_id}</TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small" color={statusColor(s.status)} />
                      </TableCell>
                      <TableCell>{fmtDate(s.start_time)}</TableCell>
                      <TableCell>{s.stop_time ? fmtDate(s.stop_time) : "—"}</TableCell>
                      <TableCell align="right">
                        {s.energy_kwh != null ? s.energy_kwh.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {s.cost != null ? `${s.cost.toFixed(2)} ${s.currency ?? ""}` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <LabelDialog
        open={addingLabel}
        entityType="driver"
        entityId={driver.id}
        onClose={() => setAddingLabel(false)}
        onSaved={() => { setAddingLabel(false); load(); }}
      />

      <ConfirmDialog
        open={deletingLabel !== null}
        title="Remove label?"
        message={`Remove label "${deletingLabel?.key}=${deletingLabel?.value}"?`}
        onCancel={() => setDeletingLabel(null)}
        onConfirm={deleteLabel}
      />

      <AuthMethodDialog
        open={authMethodDialog.open}
        driverId={driver.id}
        method={authMethodDialog.method}
        onClose={() => setAuthMethodDialog({ open: false, method: null })}
        onSaved={() => { setAuthMethodDialog({ open: false, method: null }); load(); }}
      />

      <ConfirmDialog
        open={deletingMethod !== null}
        title="Remove auth method?"
        message={`Remove auth method${deletingMethod?.label ? ` "${deletingMethod.label}"` : deletingMethod?.identifier ? ` "${deletingMethod.identifier}"` : ""}?`}
        onCancel={() => setDeletingMethod(null)}
        onConfirm={deleteAuthMethod}
      />
    </>
  );
}
